// =============================================================================
// forecastReaderCore.js — the PURE half of the Supabase forecast reader.
//
// Everything here is network-free and browser-free: it imports only src/lib/
// forecast.js (the engine proven 177/177 against the live one). It turns a
// migration-1120 `forecast_kiosk_inputs` bundle into the EXACT wrapper object the
// kitchen screen's SiteForecastSection reads from ?action=forecast.
//
// Kept separate from forecastReader.js so the same builder can be exercised in
// Node — by the parity tool (tools/lane_bg_forecast_parity.mjs) and the unit tests
// — without pulling in the Supabase client or the browser session helpers. The
// screen and the parity tool therefore build with identical code and can't drift.
//
// SCOPE: Manchester V2 / site 'tc'. Meadowhall v4.5 is out of scope.
// =============================================================================
import {
  buildForecast, contextMultipliers, applyStockoutUplift, denormalise,
  filterOutlier, canonicalSite, isExcludedItem, DEFAULT_CONFIG,
} from "./forecast.js";
import { currentNamesFor } from "./forecastMenu.js";   // Lane BQ (1322): the current menu, else the traded names

const DOW_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const isoDow = (iso) => new Date(String(iso).slice(0, 10) + "T12:00:00Z").getUTCDay();

export function forecastReadOverride(search) {
  try {
    const q = new URLSearchParams(search || "").get("forecastread");
    if (q === "on") return true;
    if (q === "off") return false;
  } catch (e) {}
  return null;
}

// The live engine picks each flag's emoji by a substring match on the label
// (getContextMultipliers → autoFlags). Cosmetic only — parity compares labels +
// mults, never emoji — but reproduced so the screen looks identical.
export function emojiForFlag(label) {
  const l = String(label || "");
  if (l.indexOf("Rain") !== -1) return "🌧️";
  if (l.indexOf("Sunny") !== -1) return "☀️";
  if (l.indexOf("Bank") !== -1) return "🏦";
  if (l.indexOf("School") !== -1) return "🏫";
  if (l.indexOf("Ramadan") !== -1) return "🌙";
  if (l.indexOf("Mother") !== -1) return "💐";
  if (l.indexOf("Black Friday") !== -1) return "🛍️";
  if (l.indexOf("Payday") !== -1) return "💰";
  if (l.indexOf("January") !== -1) return "🛒";
  if (l.indexOf("Weather") !== -1) return "❓";
  return "📌";
}

// One historical day's Square demand → the "adjusted" value the live engine feeds
// the model: stockout-uplifted (a no-op unless a sell-out time was logged — "N/A"
// on every FOH row today, so the uplift is dormant, matching the live engine) then
// de-normalised by that day's context multiplier. Reuses the SAME forecast.js
// primitives proven 177/177 — nothing is re-implemented here.
export function adjustHistoricalDemand(site, iso, demand, sellOutHour, config = DEFAULT_CONFIG) {
  if (!(demand > 0)) return demand;
  const s = canonicalSite(site);
  let hoursBeforeClose = null;
  if (sellOutHour !== null && sellOutHour !== undefined) {
    const closing = config.closingTimes?.[s]?.[isoDow(iso)];
    if (closing != null) hoursBeforeClose = closing - Number(sellOutHour);
  }
  const afterStockout = applyStockoutUplift(demand, hoursBeforeClose, config);
  const histMult = contextMultipliers(s, String(iso).slice(0, 10), {}, config).totalMultiplier;
  return denormalise(afterStockout, histMult);
}

// Merge the non-excluded per-roll history across the window into one {name: qty}
// map — the historicalMix the split proportions the day across (getSkuSplit).
export function mergeHistoricalMix(rows) {
  const mix = {};
  for (const r of rows || []) {
    const bd = r.sku_breakdown || {};
    for (const name of Object.keys(bd)) {
      if (isExcludedItem(name)) continue;
      mix[name] = (mix[name] || 0) + (Number(bd[name]) || 0);
    }
  }
  return mix;
}

// PURE. Turn the 1120 bundle into the signals/mix/waste buildForecast needs,
// reconstructing them the way getSixWeekHistory / getPreviousYearData do.
export function reconstructSignals(bundle, config = DEFAULT_CONFIG) {
  const site = bundle.site;
  const fallbackByDate = {};
  for (const f of bundle.fallback || []) fallbackByDate[f.date] = f;

  let fallbackUsed = false;
  const adjusted = [];   // most-recent first, matching history order
  for (const h of bundle.history || []) {
    let demand = h.demand;
    if (demand === null || demand === undefined) {
      // Square row absent for this week: S33 Decision 1(a) — drop to the 0284
      // stored-demand twin when present. A genuine zero-order day is source
      // 'square' and used as-is; only a truly MISSING row takes the fallback.
      const fb = fallbackByDate[h.date];
      if (fb && fb.fallback_portions != null) { demand = fb.fallback_portions; fallbackUsed = true; }
    }
    if (demand === null || demand === undefined) continue;   // no data this week
    adjusted.push(adjustHistoricalDemand(site, h.date, Number(demand), h.sell_out_hour, config));
  }

  const weeksOfData = adjusted.length;
  const sixWeekAvg = weeksOfData
    ? Math.round(adjusted.reduce((a, b) => a + b, 0) / weeksOfData)
    : null;
  const lastSameDay = weeksOfData ? filterOutlier(adjusted, sixWeekAvg, config) : null;

  const pyAdj = [];
  for (const p of bundle.prev_year || []) {
    if (!(p.demand > 0)) continue;
    pyAdj.push(adjustHistoricalDemand(site, p.date, Number(p.demand), p.sell_out_hour, config));
  }
  const prevYear = pyAdj.length ? Math.round(pyAdj.reduce((a, b) => a + b, 0) / pyAdj.length) : null;

  const historicalMix = mergeHistoricalMix(bundle.history);
  // currentNames: the live engine's menu comes from the Square catalog cache
  // (roll_name_map) — the full CURRENT menu. We have no catalog here, so we use its
  // OWN fallback path — the names that actually traded across the window
  // (buildForecast/skuSplit re-applies the always-retain list too).
  // ⚠️ FLIP BLOCKER B5 (new-menu-rolls): this is the live engine's COLD-cache path,
  // not its normal warm-cache one. A roll on the menu but with no same-weekday
  // history is OMITTED here, where the live engine lists it at newSkuMinimum — so a
  // freshly-launched special would be made ZERO (the "04/09 new rolls show 0" bug
  // re-introduced on the read-flip). Needs a Supabase current-menu source before a
  // flip. See RUNBOOK_FORECAST_ENGINE_FLIP.md (B5) and OVERNIGHT/DECISIONS_NEEDED.md.
  const currentNames = Object.keys(historicalMix);

  let waste = null;
  const wr = bundle.waste_ref;
  if (wr && wr.total_made != null && wr.waste_portions != null) {
    waste = { portionsWasted: Number(wr.waste_portions), portionsMade: Number(wr.total_made) };
  }

  // Lane BQ (1322): the bundle's `menu` (the site's active forecast_menu_items — the live
  // engine's warm roll_name_map) when it has names, else the traded names above (its
  // cold-cache path). No menu key / empty = byte-identical to before.
  return { signals: { lastSameDay, sixWeekAvg, prevYear, weeksOfData }, historicalMix, currentNames: currentNamesFor(bundle, currentNames), waste, fallbackUsed };
}

// PURE. Build the forecast from a 1120 bundle and adapt it to the EXACT wrapper
// shape ?action=forecast returns (verified against ManchesterForecastEngine.js).
export function buildForecastFromBundle(bundle, config = DEFAULT_CONFIG) {
  const site = bundle.site;
  const date = String(bundle.date).slice(0, 10);
  const { signals, historicalMix, currentNames, waste, fallbackUsed } = reconstructSignals(bundle, config);

  // The engine, exactly as proven 177/177. context {} = the DATE-DERIVED flags only
  // (school holiday, ramadan, payday, Mother's Day, Black Friday, January sales).
  //
  // ⚠️ FLIP BLOCKERS the reader CANNOT reproduce here (structural — no Supabase
  // source; forecast.js takes them as PASSED-IN params, and the 177/177 replay fed
  // recorded flags, so this path is unproven for them). See
  // RUNBOOK_FORECAST_ENGINE_FLIP.md (B3/B4) and OVERNIGHT/DECISIONS_NEEDED.md:
  //   * WEATHER (B3): the live engine fetches open-meteo and adds Rain (+0.10 TC) /
  //     Sunny (-0.08 TC) to the multiplier AND autoFlags for a forward date. No
  //     weather feed here, so a forward forecast on a rainy/sunny day is materially
  //     off. Past dates read "weather unavailable -> 0", which is why the parity
  //     tool tests past-only — that deliberately does NOT exercise this.
  //   * BANK HOLIDAYS (B4): the live engine sources gov.uk and adds +0.20 on a BH
  //     target AND divides past-BH same-weekdays out of the history. forecast.js has
  //     NO BH calendar (only schoolHolidays), so both paths silently omit it.
  // Until B3/B4 have a Supabase source, this must not be armed for forward days.
  const f = buildForecast({ site, date, signals, context: {}, waste, historicalMix, currentNames, config });

  const hasPrevYear = signals.prevYear !== null && signals.prevYear !== undefined;
  const w = config.weights;
  const wastageAdj = f.afterContext > 0 ? f.afterWaste / f.afterContext : 1;

  return {
    site,
    date,
    day: DOW_NAMES[isoDow(date)],
    inputs: {
      lastSameDay: signals.weeksOfData ? signals.lastSameDay : null,
      sixWeekAvg: signals.weeksOfData ? signals.sixWeekAvg : null,
      weeksOfData: signals.weeksOfData,
      lastWastage: 0,
      previousDayWaste: bundle.prev_day_waste || null,
      soldOut: false,
    },
    autoFlags: (f.flags || []).map((fl) => ({
      label: fl.label, mult: fl.effect, emoji: emojiForFlag(fl.label), positive: fl.effect >= 0,
    })),
    forecast: {
      weightedBase: f.weightedBase,
      tgtgBuffer: f.tgtgBuffer,
      totalPortions: f.totalPortions,
      minApplied: f.minApplied,
    },
    wastageAdjustment: f.wastageAdjustment
      ? { type: f.wastageAdjustment.type, message: f.wastageAdjustment.message }
      : null,
    calc: {
      lastSameDayWeight: w.lastSameDay,
      // The live calc block is NOT null-guarded: with no history it shows the
      // day-of-week baseline (debugInfo.sixWeekAvg / lastSameDayFiltered both fall
      // to it). f.signals.* carry that ??baseline fallback (forecast.js), so use
      // them here — inputs.* above stay null-guarded to match the live inputs block.
      lastSameDaySignal: f.signals.lastSameDay,
      sixWeekAvgWeight: hasPrevYear ? w.sixWeekAvg : w.sixWeekAvg + w.prevYear,
      sixWeekAvgSignal: f.signals.sixWeekAvg,
      prevYearWeight: hasPrevYear ? w.prevYear : 0,
      prevYearSignal: hasPrevYear ? signals.prevYear : "N/A",
      baselineWeight: w.baseline,
      baselineSignal: f.signals.baseline,
      wastageAdj,
    },
    skuBreakdown: (f.skuBreakdown || []).map((s) => ({
      name: s.name, pct: s.pct, rolls: s.rolls, isSpecial: s.isSpecial,
    })),
    dataSource: fallbackUsed ? "supabase_fallback" : "supabase",
    warnings: f.warnings || [],
    model: "V2_4signal",
  };
}

// The shape the screen renders. Anything that isn't this falls back to the sheet.
export function looksLikeAForecast(d) {
  return Boolean(
    d
    && typeof d === "object"
    && !Array.isArray(d)
    && !d.error
    && d.forecast && typeof d.forecast === "object"
    && Number.isFinite(Number(d.forecast.weightedBase))
    && Number.isFinite(Number(d.forecast.totalPortions))
    && Number.isFinite(Number(d.forecast.tgtgBuffer))
    && Array.isArray(d.autoFlags)
    && Array.isArray(d.skuBreakdown)
  );
}

// A confirm/undo mirror "disagrees with the Sheet" when the Sheet write succeeded
// but the Supabase door refused, errored, had no pass — or, on an undo, had
// NOTHING to undo (the Sheet held a confirm the twin never received: a pre-gating
// confirm, or a mirror that came back skipped/no_pass/error). 'off'/'skipped' are
// not disagreements (dual-run simply isn't running here); 'confirmed'/'undone'/
// 'exists' agree. Pure so it can be unit-tested.
export function shadowDisagrees(result) {
  return !!result && (result.status === "refused" || result.status === "error" || result.status === "no_pass" || result.status === "nothing");
}

// While the Sheet is the record, a twin status is only an ANSWER if it carries a
// row (confirmed or undone). A bare {confirmed:false} means "no row mirrored here
// yet" and must defer to the Google status, or the banner re-offers "Confirm" for
// a day the Sheet already holds (and the Sheet appends a second confirm row).
// Remove this deferral TOGETHER with the Google status fallback when the Sheet is
// retired. Pure so it can be unit-tested.
export function kioskStatusIsAnswer(d) {
  return !!d && typeof d === "object" && !d.error && !!(d.confirmed || d.undone);
}

// Is the bench fresh enough to forecast this date? If the newest Square demand row
// is more than two days before the forecast date, the six-week history ending near
// the date is stale — refuse and let the sheet answer. The honest guard that stops
// a frozen bench forecasting a live kitchen.
export function isBenchFreshEnough(bundle, date) {
  const last = bundle?.freshness?.sales_last_date;
  if (!last) return false;
  const staleAfter = new Date(String(date).slice(0, 10) + "T12:00:00Z");
  staleAfter.setUTCDate(staleAfter.getUTCDate() - 2);
  return new Date(String(last).slice(0, 10) + "T12:00:00Z") >= staleAfter;
}
